import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import {
  ShiftOptimizerGatewayService,
  UserTimelinesGateway,
} from '@wilson/api/gateway';
import { AuthState } from '@wilson/auth/core';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  AssignmentOptimizerJobStatusEnum,
  AssignmentOptimizerJobSuccessResponse,
  FeatureName,
  StartAssignmentOptimizerJobDto,
  TimelineTimeframe,
  UserTimelinesWithoutActivitiesPayload,
} from '@wilson/interfaces';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
  ShiftTimelineFilterUnassignedRegionState,
  ShiftTimelineUnassignedRegionStateFacadeService,
  TimelineFetchUnassignedShiftsWithoutActivities,
  TimelineUpdateCellViewUnassignedShiftsRegionWithoutActivities,
} from '@wilson/shift-timeline/state';
import { getTimeZoneCorrectedDateRange } from '@wilson/utils';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { firstValueFrom } from 'rxjs';
import { ShiftTimelineDraftHelperService } from '../../../services/shift-timeline-draft-helper/shift-timeline-draft-helper.service';
import { AssignmentOptimizerSettingsComponent } from '../../drawers/timeline-assignment-optimizer-settings-drawer/assignment-optimizer-settings-drawer.component';
import { AssignAllShiftsButtonViewComponent } from '../assign-all-shifts-button-view/assign-all-shifts-button-view.component';
import { AssignAllShiftsButtonService } from '../assign-all-shifts-button-view/assign-all-shifts-button.service';
import { areIntervalsOverlapping } from 'date-fns';
@Component({
  selector: 'wilson-assign-all-shift-without-activities-button',
  imports: [
    CommonModule,
    AssignAllShiftsButtonViewComponent,
    TranslateModule,
    NzButtonModule,
    NzSpinModule,
    NzModalModule,
    NzDropDownModule,
    FeatureFlagsModule,
    AuthorizationModule,
    AssignmentOptimizerSettingsComponent,
  ],
  templateUrl: './assign-all-shifts-without-activities-button.component.html',
  styleUrl: './assign-all-shifts-without-activities-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignAllShiftsWithoutActivitiesButtonComponent {
  protected assignAllShiftsButtonService = inject(AssignAllShiftsButtonService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private nzMessageService = inject(NzMessageService);
  private shiftTimelineDraftHelperService = inject(
    ShiftTimelineDraftHelperService,
  );
  private readonly router = inject(Router);
  private readonly cdRef = inject(ChangeDetectorRef);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  private shiftOptimizerGatewayService = inject(ShiftOptimizerGatewayService);
  private userTimelineGateway = inject(UserTimelinesGateway);
  private shiftTimelineUnassignedRegionStateFacadeService = inject(
    ShiftTimelineUnassignedRegionStateFacadeService,
  );

  public isLoadingDraftMode = false;

  protected FeatureName = FeatureName;
  @Input() isLoadingUsers = false;

  async createAssignmentJob(): Promise<void> {
    this.isLoadingDraftMode = true;
    this.cdRef.detectChanges();
    try {
      const numberOfReleasedShifts =
        await this.assignAllShiftsButtonService.getNumberOfReleasedShiftWithoutActivitiesAssignmentsInView();

      if (numberOfReleasedShifts > 0) {
        const userModalResponse = await firstValueFrom(
          this.assignAllShiftsButtonService.confirmIncludesReleasedShifts(
            numberOfReleasedShifts,
          ).afterClose,
        );
        if (userModalResponse) {
          await this.initializeAndFetchShiftAssignments();
        } else {
          this.isLoadingDraftMode = false;
          this.cdRef.detectChanges();
        }
      } else {
        await this.initializeAndFetchShiftAssignments();
      }
    } catch {
      this.isLoadingDraftMode = false;
      this.cdRef.detectChanges();
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  private async initializeAndFetchShiftAssignments(): Promise<void> {
    // visible date range can change while all data is being fetched, so we need the same range for all operations
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );

    let isCellView = false;
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      const isCellViewData = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.isCellView(userId),
      );
      isCellView = isCellViewData;
    }

    const { shiftAssignments, absenceAssignments, unassignedShifts } =
      await this.getAllUsersData({ visibleDateRange, isCellView });

    const shiftAssignmentPayload =
      this.assignAllShiftsButtonService.initializeShiftAssignmentJob({
        visibleDateRange,
        shiftAssignments,
        absenceAssignments,
        unassignedShifts,
        isCellView,
      });

    await this.fetchShiftAssignmentSuggestions(shiftAssignmentPayload);
  }

  private async fetchShiftAssignmentSuggestions(
    payload: StartAssignmentOptimizerJobDto | null,
  ): Promise<void> {
    if (payload) {
      try {
        await firstValueFrom(
          this.shiftOptimizerGatewayService.createShiftAssignmentOptimizerJob(
            payload,
          ),
        );

        const shiftOptimizerJobResponse = await this.checkIfJobIsSucceeded(
          payload.jobId,
        );

        if (shiftOptimizerJobResponse) {
          await this.shiftTimelineDraftHelperService.setShiftAssignmentOptimizerJobResponse(
            shiftOptimizerJobResponse,
          );
          this.router.navigateByUrl('/shift-timeline/draft');
        }
      } catch (error) {
        this.isLoadingDraftMode = false;
        this.cdRef.detectChanges();
        captureException(error);
        this.nzMessageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    } else {
      this.isLoadingDraftMode = false;
      this.cdRef.detectChanges();
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  private async checkIfJobIsSucceeded(
    jobId: string,
  ): Promise<AssignmentOptimizerJobSuccessResponse | null> {
    const response = await firstValueFrom(
      this.shiftOptimizerGatewayService.getShiftAssignmentOptimizerJob(jobId),
    );

    if (response.status === AssignmentOptimizerJobStatusEnum.SUCCEEDED) {
      return response;
    } else if (response.status === AssignmentOptimizerJobStatusEnum.PENDING) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      return await this.checkIfJobIsSucceeded(jobId);
    } else {
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.isLoadingDraftMode = false;
      this.cdRef.detectChanges();
      return null;
    }
  }

  private async getAllUsersData({
    visibleDateRange,
    isCellView,
  }: {
    visibleDateRange: TimelineTimeframe;
    isCellView: boolean;
  }): Promise<{
    unassignedShifts: string[];
    shiftAssignments: Record<string, string[]>;
    absenceAssignments: Record<string, string[]>;
  }> {
    this.isLoadingDraftMode = true;
    const filteredUsers = await firstValueFrom(
      this.shiftTimelineFilterStateService.filteredUsers$,
    );
    const filteredUsersIds = filteredUsers.map((user) => user.id) as string[];
    let shiftAssignments: Record<string, string[]> = {};
    let absenceAssignments: Record<string, string[]> = {};
    try {
      if (
        filteredUsersIds.length > 0 &&
        visibleDateRange.start < visibleDateRange.end
      ) {
        const correctedInterval =
          getTimeZoneCorrectedDateRange(visibleDateRange);
        const options =
          await this.shiftTimelineFilterStateService.generateFilterOptionsForAssignedRegion(
            correctedInterval.start,
            correctedInterval.end,
            filteredUsersIds,
          );
        const users = await firstValueFrom(
          this.userTimelineGateway.getUserTimelinesWithoutActivities(options),
        );
        const groupedShiftsAndAbsences =
          await this.assignAllShiftsButtonService.mapUserShiftAndAbsenceAssignments(
            {
              visibleDateRange,
              userTimelinesData: users.reduce(
                (acc, user) => {
                  acc[user.id as string] = user;
                  return acc;
                },
                {} as Record<string, UserTimelinesWithoutActivitiesPayload>,
              ),
            },
          );
        shiftAssignments = groupedShiftsAndAbsences.shiftAssignments;
        absenceAssignments = groupedShiftsAndAbsences.absenceAssignments;
      }

      const unassignedShifts = await this.getUnassignedShifts(
        isCellView,
        visibleDateRange,
      );

      return {
        unassignedShifts,
        shiftAssignments,
        absenceAssignments,
      };
    } catch {
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.isLoadingDraftMode = false;
      return {
        unassignedShifts: [],
        shiftAssignments: {},
        absenceAssignments: {},
      };
    }
  }

  private async getUnassignedShifts(
    isCellView: boolean,
    visibleDateRange: TimelineTimeframe,
  ): Promise<string[]> {
    try {
      const dispatchAction = isCellView
        ? new TimelineUpdateCellViewUnassignedShiftsRegionWithoutActivities(
            visibleDateRange,
          )
        : new TimelineFetchUnassignedShiftsWithoutActivities(visibleDateRange);

      await firstValueFrom(this.store.dispatch(dispatchAction));

      const [unassignedShiftIds, unassignedShiftSearches] = await Promise.all([
        firstValueFrom(
          this.store.select(
            ShiftTimelineFilterUnassignedRegionState.unassignedShiftIds,
          ),
        ),
        firstValueFrom(
          this.store.select(
            ShiftTimelineFilterUnassignedRegionState.unassignedShiftSearches,
          ),
        ),
      ]);

      if (isCellView) {
        const unassignedShiftsWithoutActivitiesOverlapLevels =
          await firstValueFrom(
            this.shiftTimelineUnassignedRegionStateFacadeService
              .unassignedShiftsWithoutActivitiesOverlapLevels$,
          );

        const filteredShiftStacks =
          ShiftTimelineFilterUnassignedRegionState.filterUnassignedShiftStacks(
            unassignedShiftsWithoutActivitiesOverlapLevels,
            unassignedShiftIds,
            unassignedShiftSearches,
          );

        const filteredShiftIds = filteredShiftStacks.flatMap((shift) => {
          return Object.values(shift.shifts).reduce((acc, shiftData) => {
            const isOverlapping = areIntervalsOverlapping(
              {
                start: new Date(shiftData.startDatetime),
                end: new Date(shiftData.endDatetime),
              },
              {
                start: new Date(visibleDateRange.start),
                end: new Date(visibleDateRange.end),
              },
            );
            if (isOverlapping) {
              acc.push(shiftData.id);
            }
            return acc;
          }, [] as string[]);
        });

        return filteredShiftIds;
      } else {
        const unassignedShiftsWithoutActivities = await firstValueFrom(
          this.shiftTimelineUnassignedRegionStateFacadeService
            .unassignedShiftsWithoutActivities$,
        );

        const filteredShifts =
          ShiftTimelineFilterUnassignedRegionState.filterUnassignedShift(
            unassignedShiftsWithoutActivities,
            unassignedShiftIds,
            unassignedShiftSearches,
          );

        const filteredShiftIds = filteredShifts.reduce((acc, shift) => {
          const isOverlapping = areIntervalsOverlapping(
            {
              start: new Date(shift.startDatetime),
              end: new Date(shift.endDatetime),
            },
            {
              start: new Date(visibleDateRange.start),
              end: new Date(visibleDateRange.end),
            },
          );

          if (isOverlapping) {
            acc.push(shift.id);
          }
          return acc;
        }, [] as string[]);

        return filteredShiftIds;
      }
    } catch (error) {
      captureException(error);
      return [];
    }
  }
}
